import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { BreakpointObserver } from "@angular/cdk/layout";
import { combineLatest, Subscription } from "rxjs";
import { AngularSvgIconModule } from "angular-svg-icon";
import { PipesModule } from "../../pipes/pipes.module";
import { ZoomService } from "../../action-menu/zoom/zoom.service";
import { FlipCoachTargetDirective } from "../../coach-mark/coach-mark-target.directive";

const MOBILE_QUERY = "(max-width: 767px)";
const TRUNCATION_ELLIPSIS = "...";
const TRUNCATE_AT = 5;

type LinesClass = "" | "lines-1" | "lines-2" | "lines-3" | "lines-4" | "lines-5";

@Component({
  selector: "app-card-content",
  templateUrl: "./card-content.component.html",
  styleUrls: ["./card-content.component.scss"],
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule, PipesModule, FlipCoachTargetDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
// Pure display component for card text, period, flip, and editable placeholder.
// flip state is owned by the typed card component (parent); this component emits events and receives the current state.
export class CardContentComponent implements OnChanges, OnDestroy {
  constructor(
    private cdr: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver,
    private zoomService: ZoomService,
  ) {}

  /** Pre-processed HTML string (URLs replaced by typed card component). */
  @Input() cardHtml: string = "";
  @Input() periodMinutes: number = 0;
  /** Controlled by parent typed card component. */
  @Input() flip: "active" | "inactive" = "inactive";
  @Input() cardDescription: string | null = null;
  @Input() editMode: boolean = false;
  @Input() isEditable: boolean = false;
  @Input() isCodeCard: boolean = false;
  /** True when this is a custom card that hasn't been named yet (cardName === 'blank'). Controls editable placeholder visibility. */
  @Input() isUneditedCustom: boolean = false;
  /** Drives edit-icon colour class ('activity', 'activity_recommended'). */
  @Input() cardTypeCss: string = "";
  @Input() showFlipIcon: boolean = false;
  /** Onboarding UP05 (FLIP-386): coach-mark anchor for the flip icon. */
  @Input() flipAnchor: string | null = null;

  @Output() editClicked = new EventEmitter<void>();
  @Output() flipClicked = new EventEmitter<void>();

  // `#cardTextRef` lives inside *ngIf="cardHtml" — for a fresh custom card it's absent
  // until the user saves an edit. A setter (not a plain @ViewChild field) lets us wire up
  // the ResizeObserver / fonts / zoom subscription whenever the element first attaches,
  // including the placeholder→edited transition that happens long after ngAfterViewInit.
  private cardTextEl?: ElementRef<HTMLElement>;
  @ViewChild("cardTextRef") set cardTextRef(el: ElementRef<HTMLElement> | undefined) {
    if (this.cardTextEl === el) return;
    this.detachFromCardTextEl();
    this.cardTextEl = el;
    if (!el || this.destroyed) return;
    this.attachToCardTextEl();
  }

  linesClass: LinesClass = "";
  private resizeObserver?: ResizeObserver;
  private contextSub?: Subscription;
  private originalText: string | null = null;
  private mutating = false;
  private measureRetryHandle = 0;
  private measureRetryCount = 0;
  private destroyed = false;

  private attachToCardTextEl(): void {
    if (!this.cardTextEl) return;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.mutating) return;
      this.updateLineCountClass();
    });
    this.resizeObserver.observe(this.cardTextEl.nativeElement);

    // bc-theme.scss loads Barlow Condensed via Google Fonts with font-display:
    // swap, so first paint uses fallback metrics. ResizeObserver won't refire
    // on font-swap (cardTextEl's outer size is CSS-fixed), and document.fonts.ready
    // can resolve before the swap has been applied to this element — use the
    // explicit fonts.load(spec) form which resolves only after that face is
    // loaded, then take one extra frame so layout settles before re-measuring.
    const fonts = typeof document !== "undefined" ? (document as any).fonts : null;
    if (fonts?.load) {
      Promise.resolve(fonts.load("600 1em 'Barlow Condensed'"))
        .catch(() => fonts.ready)
        .then(() => {
          if (this.destroyed || !this.cardTextEl) return;
          this.updateLineCountClass();
          if (this.measureRetryHandle) cancelAnimationFrame(this.measureRetryHandle);
          this.measureRetryHandle = requestAnimationFrame(() => {
            this.measureRetryHandle = 0;
            if (this.destroyed || !this.cardTextEl) return;
            this.updateLineCountClass();
          });
        });
    }

    // Re-measure on breakpoint / zoom *changes* only. The initial synchronous emission
    // (zoomService is a BehaviorSubject) is skipped via the change-flag — mutating the
    // lines class during the in-flight change-detection cycle would otherwise trip NG0100.
    this.contextSub?.unsubscribe();
    let prevMatches: boolean | undefined;
    let prevZoom: number | undefined;
    this.contextSub = combineLatest([
      this.breakpointObserver.observe(MOBILE_QUERY),
      this.zoomService.zoomLevel$,
    ]).subscribe(([state, zoom]) => {
      const changed = prevMatches !== undefined && (state.matches !== prevMatches || zoom !== prevZoom);
      prevMatches = state.matches;
      prevZoom = zoom;
      if (changed) this.updateLineCountClass();
    });

    // ResizeObserver delivers an initial entry after observe() (per spec), which
    // triggers updateLineCountClass(). The font.load callback above provides the
    // second pass once Barlow Condensed swaps in. No explicit initial call needed.
  }

  private detachFromCardTextEl(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.contextSub?.unsubscribe();
    this.contextSub = undefined;
    if (this.measureRetryHandle) {
      cancelAnimationFrame(this.measureRetryHandle);
      this.measureRetryHandle = 0;
    }
    this.measureRetryCount = 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["cardHtml"] || changes["periodMinutes"]) {
      this.originalText = null;  // input changed — re-cache on next measure
      if (this.cardTextEl) this.updateLineCountClass();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.detachFromCardTextEl();
  }

  private updateLineCountClass(): void {
    if (!this.cardTextEl) return;
    const inner = this.cardTextEl.nativeElement.querySelector("div") as HTMLElement | null;
    if (!inner) return;

    // When rendered inside a not-yet-laid-out container (e.g. cdk-virtual-scroll-viewport
    // on first paint), scrollHeight is 0 and the measurement below would mis-read the line
    // count. Retry on the next frame instead; ResizeObserver won't re-fire because the outer
    // element's size is fixed by CSS, not by content.
    if (inner.scrollHeight === 0 || inner.offsetParent === null) {
      if (this.measureRetryCount < 5) {
        this.measureRetryCount++;
        if (this.measureRetryHandle) cancelAnimationFrame(this.measureRetryHandle);
        this.measureRetryHandle = requestAnimationFrame(() => {
          this.measureRetryHandle = 0;
          this.updateLineCountClass();
        });
      }
      return;
    }
    this.measureRetryCount = 0;

    // Media cards have a fixed short label; preserve their structure as-is.
    const isMediaCard = inner.classList.contains("media-title");
    // The expanded-card dialog scales the whole card ~3× via --card-font-size,
    // so there is room for the full text. Skip the `...` truncation in that context.
    const isExpanded = !!inner.closest(".card-expanded-host");

    // Restore the full text before measuring so a previous truncation doesn't
    // mask the real line count (e.g. when the viewport grows). Re-derive it from the
    // current input first: a stale cache here is what made a swapped card keep the
    // name it was replacing (FLIP-227).
    if (!isMediaCard) {
      this.originalText = this.textFromCardHtml();
      this.setText(inner, this.originalText);
    }

    // Single fixed font-size (no word-count tiers): the only adjustment is clamping
    // overly-long names to TRUNCATE_AT rows with a trailing ellipsis.
    if (!isMediaCard && !isExpanded) {
      this.truncateToFit(inner, TRUNCATE_AT);
    }

    const lh = parseFloat(getComputedStyle(inner).lineHeight) || 16;
    const lines = lh > 0 ? Math.round(inner.scrollHeight / lh) : 0;
    const nextLines = Math.min(lines || TRUNCATE_AT, TRUNCATE_AT);

    const nextLinesCls: LinesClass = nextLines > 0
      ? (`lines-${nextLines}` as LinesClass)
      : "";
    if (nextLinesCls !== this.linesClass) {
      this.linesClass = nextLinesCls;
      this.cdr.markForCheck();
    }
  }

  /** The card's full text, taken from the `cardHtml` input rather than scraped from the DOM.
   *
   *  ngOnChanges runs *before* Angular flushes this component's own `[innerHtml]` binding, so
   *  `inner.textContent` there is still the previous card's name. Caching that and restoring it
   *  on a later measure put the old name back over the newly rendered one — FLIP-227's
   *  "swapped card does not appear until the plan is saved". The input is the source of truth
   *  and carries no such ordering hazard. */
  private textFromCardHtml(): string {
    const scratch = document.createElement("div");
    scratch.innerHTML = this.cardHtml ?? "";
    return scratch.textContent ?? "";
  }

  /** Trim text content with " ..." suffix until it fits in `maxLines` at the current font-size. */
  private truncateToFit(inner: HTMLElement, maxLines: number): void {
    if (this.originalText === null) this.originalText = this.textFromCardHtml();
    const full = this.originalText;
    if (!full) return;

    const lh = parseFloat(getComputedStyle(inner).lineHeight) || 16;
    if (lh <= 0) return;
    const maxHeight = lh * maxLines;

    // Already fits — keep full text.
    this.setText(inner, full);
    if (inner.scrollHeight <= maxHeight) return;

    // Binary-search the largest prefix length whose `prefix + ellipsis` still fits.
    let lo = 0;
    let hi = full.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi + 1) / 2);
      this.setText(inner, full.slice(0, mid).replace(/\s+$/, "") + TRUNCATION_ELLIPSIS);
      if (inner.scrollHeight <= maxHeight) lo = mid;
      else hi = mid - 1;
    }

    // Walk back to the last whitespace so we don't cut mid-word.
    let cut = lo;
    while (cut > 0 && !/\s/.test(full.charAt(cut - 1))) cut--;
    if (cut === 0) cut = lo; // single very long word — keep the hard cut

    this.setText(inner, full.slice(0, cut).replace(/\s+$/, "") + TRUNCATION_ELLIPSIS);
  }

  /** Write textContent under a guard so the ResizeObserver doesn't recurse. */
  private setText(inner: HTMLElement, text: string): void {
    if (inner.textContent === text) return;
    this.mutating = true;
    inner.textContent = text;
    // ResizeObserver delivers asynchronously; clear the flag on the next microtask.
    queueMicrotask(() => { this.mutating = false; });
  }
}
